import { TripFareHeader } from './TripFareColumns';
import FareTripCard from './FareTripCard';
import { passengerCountsByType } from '../../BookingWizardData/BaseBookingWizardData';
import { appConfigStore } from 'Models/AppConfigStore';
import { TripClassPrice } from 'Services/Api/_HumanWritten/PricingService/PricingService';
import React from 'react';
import moment from 'moment';
import { store } from 'Models/Store';
import { BookingWizardData } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/BookingWizardData/BaseBookingWizardData';
import { TicketsTabTrip } from 'Services/Api/_HumanWritten/BookingWizardDataService';
import { FormattedMessage } from 'react-intl';
import TripOption from './TripOption/TripOption';
import TripOptionDepartedGroup from './TripOption/TripOptionDepartedGroup';
import { selectedClassId } from './TripFareColumns';
import { mixSummary, mixedTravellers, windowClasses } from './mixedFares';
import { wizardModeOptions } from 'Models/Enums';

interface FilteredTripCardsProps {
	list: TicketsTabTrip[];
	wizardData: BookingWizardData;
	saveChanges: (newData: BookingWizardData) => void;
	departure: boolean;
	priceByFerryTrip: { [key: string]: number };
	/** Per-class fares for the window, keyed by trip id. Empty where the vessel has no classes. */
	classPricesByFerryTrip?: { [key: string]: TripClassPrice[] };
	loading: boolean;
	refreshTrips?: () => void;
}

export const FilteredTripCards = ({
	list,
	wizardData,
	departure,
	saveChanges,
	priceByFerryTrip,
	classPricesByFerryTrip,
	loading,
	refreshTrips,
}: FilteredTripCardsProps) => {
	const trips = list.filter(x => x.startDate);
	const counts = passengerCountsByType(wizardData);

	// One set of columns for the day, taken from the first sailing that has classes. A window's
	// sailings run on the same vessels, so the classes are the same; a day where they are not still
	// draws a cell per column because the cells are keyed by class, not by position.
	const columns = trips
		.map(trip => classPricesByFerryTrip?.[trip.id] ?? [])
		.find(classPrices => classPrices.length > 0) ?? [];

	// The setting decides whether this list is a fare table at all — the header, the columns, the day
	// rail above it. It does NOT decide what an individual sailing is drawn as: see `fareCardFor`.
	//
	// Reading the store during render is safe: `appConfigStore` is built once at module load from
	// `window.configData`, which the page carries before React starts and never changes. (An earlier
	// pass here worried about a stale read on a store that is not observable. Nothing to be stale
	// from — it is not fetched.)
	const showFareColumns = appConfigStore.passengerClassesEnabled;

	/**
	 * Whether THIS sailing is drawn as a fare card.
	 *
	 * <p>The fare card's only way in is a fare cell: choosing a class is what selects the sailing, so
	 * a card drawn with no classes has nothing on it to click and the sailing cannot be booked at
	 * all. A vessel with no fare types configured, a trip with no vessel, a journey the pricing
	 * engine returns no price list for — each of them yields an empty column set, and each of them
	 * left the whole day's sailings inert.</p>
	 *
	 * <p>So the setting turns the fare table on, and the sailing's own fares decide whether it is a
	 * row of that table or the plain card it has always been. The day keeps its rail and its header
	 * either way.</p>
	 */
	const fareCardFor = (trip: TicketsTabTrip) =>
		showFareColumns && (classPricesByFerryTrip?.[trip.id]?.length ?? 0) > 0;

	const legType = departure ? 'departure' : 'return';

	// Editing a booking rather than making one. Every figure on the cards below becomes a difference
	// against what is already paid — see FareTripCard's `alterationDelta`.
	const isAlteration = wizardData.wizardMode === wizardModeOptions.ALTERATION;

	/**
	 * Whether the booking arrives at this list already split across fare types.
	 *
	 * <p>Said once, above the day, because it is a fact about the booking rather than about any one
	 * sailing — and because every cell in the table below means "put everyone in this", which is not
	 * what a booker changing their departure time is asking for. Each card then carries the part that
	 * is its own: what keeping the split costs on that sailing.</p>
	 */
	const mixed = showFareColumns && selectedClassId(wizardData, legType) === 'MIXED';

	// `2 Comfort · 1 Premium`, under the column that offers to keep it. Asked once here rather than
	// per sailing: it describes the booking, and the cards all draw the same answer.
	const mixLabel = React.useMemo(
		() => (mixed
			? mixSummary(mixedTravellers(wizardData, legType), windowClasses(columns, classPricesByFerryTrip))
			: null),
		[mixed, wizardData, legType, columns, classPricesByFerryTrip],
	);

	/*
		The sailings that have gone, folded into one line — but only when something is left.

		Staff may book a departed sailing, so these rows stay reachable; on a day that is half over
		they were simply in the way, pushing what can still be sold below the fold. On a day whose
		every sailing has gone they are not in the way of anything — they are the day — so the group
		is not built and the list is drawn as it always was. That single condition is what keeps this
		off previous days without having to ask what date is being looked at.

		Read from the trip's own flag and its time, the same pair the row's own `Departed` note uses,
		so a row cannot sit outside a group that describes it.
	*/
	const isDeparted = (trip: TicketsTabTrip) =>
		trip.departed || moment(trip.departureDateTime).isBefore(moment());
	const departedTrips = trips.filter(isDeparted);
	const groupDeparted = store.isStaff && departedTrips.length > 0 && departedTrips.length < trips.length;

	const renderTrip = (trip: TicketsTabTrip) => {
		const isSelected = departure
			? wizardData.departureTicketId === trip.id
			: wizardData.returningTicketId === trip.id;
		const asFareCard = fareCardFor(trip);

		return (
			<React.Fragment key={`${trip.id}-${trip.startStopId}-${trip.endStopId}`}>
							{/*
								Two cards, not one card with a row bolted on. A vessel with priced
								classes gets FareTripCard, whose grid the header above also uses;
								everything else gets TripOption, which draws the same sailing in the
								redesigned styling. Both rule their own hairline, so the separator
								that used to sit between plain cards would now double it.
							*/}
							{asFareCard ? (
								<FareTripCard
									trip={trip}
									classPrices={classPricesByFerryTrip?.[trip.id] ?? []}
									wizardData={wizardData}
									saveChanges={saveChanges}
									legType={legType}
									isSelected={isSelected}
									mixLabel={mixLabel}
									alterationDelta={isAlteration ? priceByFerryTrip[trip.id] ?? null : null}
									refreshTrips={refreshTrips}
								/>
							) : (
								<TripOption
									legType={legType}
									trip={trip}
									price={priceByFerryTrip[trip.id]}
									isSelected={isSelected}
									isLoading={loading}
									refreshTrips={refreshTrips}
								/>
							)}
			</React.Fragment>
		);
	};

	if (trips.length > 0) {
		return (
			<>
				{showFareColumns && (
					<TripFareHeader
						classPrices={columns}
						counts={counts}
						mix={mixLabel}
						isAlteration={isAlteration}
					/>
				)}
				{groupDeparted && (
					<TripOptionDepartedGroup count={departedTrips.length}>
						{departedTrips.map(renderTrip)}
					</TripOptionDepartedGroup>
				)}
				{trips.filter(trip => !groupDeparted || !isDeparted(trip)).map(renderTrip)}
			</>
		);
	}
	return (
		<div className="no-trips-available">
			<FormattedMessage id="ferry-schedule__no-trips-available__label" defaultMessage="No trips available" />
		</div>
	);
};
